import React, { useMemo } from 'react';

import { Box } from '@wds/box';

import { getCoreCostFromSkus } from '~app/pages/Pricing/lib/lib-helpers';
import { logAmplitudeEvent } from '~app/pages/Pricing/pages/shared';
import { usePricingState } from '~shared/hooks/usePricingState';
import { PackageIds, Packages } from '~shared/constants/pricing';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import ACTIONS from '~shared/actions';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';
import { CombinedActions } from '~shared/types/pricing/actions';
import { Grow2792ToggleProps } from '../../types';
import { PACKAGE_SWAP } from '../../constants';

import useStyles from './useStyles';

export default function ToggleSwitch({ packageName }: Grow2792ToggleProps): JSX.Element {
  const {
    state: {
      packages: { allPackages },
      grow2792: { toggleChecked },
    },
    dispatch,
  } = usePricingState();

  const { Switch, Values, Checked } = useStyles();
  const { pageName } = useTrackingDetails();

  function handleChange(): void {
    dispatch({
      type: ACTIONS.PNP_REFRESH_TOGGLE_SWITCH,
      payload: {
        packageToBeReplaced: packageName,
        replacedWith:
          packageName === Packages.TeamAdvantage
            ? Packages.AdvantageMonthly
            : PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.packageName,
      },
    } as CombinedActions);

    const packageSwap = {
      packageToBeReplaced:
        packageName === Packages.TeamAdvantage
          ? PackageIds.TEAM_ADVANTAGE_ANNUAL
          : PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].id,
      replacedWith:
        packageName === Packages.TeamAdvantage
          ? PackageIds.ADVANTAGE_MONTHLY
          : PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.id,
    };
    dispatch({ type: ACTIONS.SWITCH_PACKAGES, payload: packageSwap } as CombinedActions);

    if (packageName === Packages.TeamAdvantage) {
      dispatch({ type: ACTIONS.PNP_REFRESH_RESET_TOGGLE_SWITCH } as CombinedActions);
    }

    logAmplitudeEvent({
      eventName: 'pricing clicked toggle',
      pageName,
      amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
      otherData: {
        selectedPackage:
          packageName === Packages.TeamAdvantage
            ? Packages.AdvantageMonthly
            : PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.packageName,
      },
    });
  }
  const savingsPercentage = useMemo(() => {
    let annualPackage = null;
    let monthlyPackage = null;

    switch (packageName) {
      case Packages.FormsAnnual:
      case Packages.FormsMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsMonthly);
        break;

      case Packages.TeamAdvantage:
      case Packages.AdvantageAnnual:
      case Packages.AdvantageMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.AdvantageAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.AdvantageMonthly);
        break;
    }

    let percentage = 0;

    if (annualPackage && monthlyPackage) {
      const annualSkuCost = getCoreCostFromSkus(annualPackage.skuCost);
      const monthlySkuCost = getCoreCostFromSkus(monthlyPackage.skuCost);

      if (annualSkuCost && monthlySkuCost) {
        percentage = (annualSkuCost.cost / (monthlySkuCost.cost * 12)) * 100 - 100;
      }
    }

    return Math.floor(-percentage);
  }, [allPackages, packageName]);

  const isChecked =
    toggleChecked?.includes(packageName) &&
    (packageName === Packages.AdvantageAnnual ||
      packageName === Packages.FormsAnnual ||
      packageName === Packages.TeamAdvantage);

  const id = `${packageName}-toggle`;
  const annualSavings = savingsPercentage ? `(Save ${savingsPercentage}%)` : null;

  return (
    <label htmlFor={id}>
      <Box className={`switch ${Switch}`}>
        <input
          role="switch"
          aria-checked="false"
          id={id}
          tabIndex={0}
          type="checkbox"
          defaultChecked={isChecked}
          onChange={handleChange}
        />
        <Box className={`${Values} ${isChecked ? Checked : ''}`}>
          <span>Monthly</span>
          <span>
            Annually <strong>{annualSavings}</strong>
          </span>
        </Box>
      </Box>
    </label>
  );
}
